import { createConnectQueryKey, useMutation, useQuery } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'

import { getControlQueryKey, getControlTimelineQueryKey } from '@/api/add-comment.hook'
import { getGetCompanyQueryKey } from '@/api/company.hook'
import { REFETCH_INTERVAL_MS } from '@/api/consts'
import { getListQuestionnairesQueryKey } from '@/api/optimistic-update-questionnaire.hook'
import { Company } from '@/gen/inventory/v1/company_service_pb'
import {
  deleteQuestionnaire,
  getInfoQuestionnaire,
  getPortalCommentsRoom,
  getRiskQuestionnaire,
  inviteRecipients,
  listQuestionnaires,
  removeRecipients,
  sendManualReminder,
  submitManualQuestionnaire,
  submitManualQuestionnaireActivity,
  updateManualQuestion,
  updateQuestionnaire,
} from '@/gen/questionnaire/v2/questionnaire-QuestionnaireService_connectquery'
import { QuestionnaireService } from '@/gen/questionnaire/v2/questionnaire_pb'

import { useTrackCallback } from '@/lib/analytics/events'
import { defaultTransport } from '@/lib/connect/connect-transport'

export const useUpdateManualQuestion = () => {
  return useMutation(updateManualQuestion)
}

export const useSubmitManualQuestionnaire = () => {
  const queryClient = useQueryClient()
  return useMutation(submitManualQuestionnaire, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getInfoQuestionnaireQueryKey() })
    },
  })
}

export const useSubmitManualQuestionnaireActivity = () => {
  const queryClient = useQueryClient()

  return useMutation(submitManualQuestionnaireActivity, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getInfoQuestionnaireQueryKey() })
    },
  })
}

export const getAssessorListQuestionnairesQueryKey = (
  companyId?: Company['id'],
  ongoingAssessmentId?: string,
) =>
  createConnectQueryKey({
    transport: defaultTransport,
    schema: QuestionnaireService.method.listQuestionnaires,
    input: { companyId, ongoingAssessmentId },
    cardinality: 'finite',
  })

export const useListAssessorQuestionnaires = ({
  companyId,
  ongoingAssessmentId,
  refetchInterval,
  enabled = true,
}: {
  companyId?: Company['id']
  ongoingAssessmentId?: string
  refetchInterval?: number
  enabled?: boolean
}) => {
  return useQuery(
    listQuestionnaires,
    {
      companyId,
      ongoingAssessmentId,
    },
    {
      ...(refetchInterval ? { refetchInterval } : null),
      enabled,
    },
  )
}

export const useUpdateQuestionnaire = () => {
  const queryClient = useQueryClient()
  return useMutation(updateQuestionnaire, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
      // Sharing seeds control thread activities, so refresh the per-control
      // timeline and control queries that render them.
      queryClient.invalidateQueries({ queryKey: getControlTimelineQueryKey() })
      queryClient.invalidateQueries({ queryKey: getControlQueryKey() })
    },
  })
}

export const useGetRiskQuestionnaire = ({ questionnaireId }: { questionnaireId: string }) => {
  return useQuery(getRiskQuestionnaire, { questionnaireId })
}

export const useGetInfoQuestionnaire = ({ questionnaireId }: { questionnaireId: string }) => {
  return useQuery(
    getInfoQuestionnaire,
    { questionnaireId },
    { refetchInterval: REFETCH_INTERVAL_MS },
  )
}

export const useInviteRecipient = () => {
  const queryClient = useQueryClient()

  return useMutation(inviteRecipients, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
    },
  })
}

export const useRemoveRecipients = () => {
  const queryClient = useQueryClient()
  const trackRecipientsRemove = useTrackCallback('questionnaire.recipients.remove')

  return useMutation(removeRecipients, {
    onSuccess: (_data, variables) => {
      trackRecipientsRemove({
        orgCompanyId: variables.orgCompanyId,
        recipient_count: variables.emails?.length ?? 0,
      })
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
      queryClient.invalidateQueries({ queryKey: getGetCompanyQueryKey() })
    },
  })
}

export const useSendManualReminder = () => {
  return useMutation(sendManualReminder)
}

export const useDeleteQuestionnaire = () => {
  const queryClient = useQueryClient()
  const trackQuestionnaireDelete = useTrackCallback('questionnaire.delete')

  return useMutation(deleteQuestionnaire, {
    onSuccess: (_data, variables) => {
      trackQuestionnaireDelete({ questionnaireId: variables.questionnaireId })
      queryClient.invalidateQueries({ queryKey: getListQuestionnairesQueryKey() })
    },
  })
}

export const getInfoQuestionnaireQueryKey = (questionnaireId?: string) =>
  createConnectQueryKey({
    schema: QuestionnaireService.method.getInfoQuestionnaire,
    input: { questionnaireId },
    transport: defaultTransport,
    cardinality: 'finite',
  })

// ID tokens can only enter rooms that already exist, so the room has to be created
// before mounting a RoomProvider for it. The server returns the room id to connect to.
export const useGetPortalCommentsRoom = (orgCompanyId?: string) =>
  useQuery(getPortalCommentsRoom, { orgCompanyId }, { enabled: !!orgCompanyId })
